import { Injectable } from '@angular/core';
import CustomStore from 'devextreme/data/custom_store';
import { DataSourceOptions } from 'devextreme/data/data_source';
import { LoadOptions } from 'devextreme/data/load_options';
import { compact, isNil } from 'lodash-es';
import groupBy from 'lodash-es/groupBy';
import isEmpty from 'lodash-es/isEmpty';
import sum from 'lodash-es/sum';
import moment, { utc } from 'moment/moment';
import { Options, RRule } from 'rrule';
import { oc } from 'ts-optchain';
import {
  gqlMongoByKey,
  gqlMongoCount,
  gqlMongoLoad,
} from '../../../shared/classes/loopback-custom-store/generic/store.utils';
import { isAlpha } from '../../../shared/classes/utils/utils';
import { DataSourceService } from '../../../shared/modules/my-common/services/datasource.service';
import { Facility } from '../../../shared/sdk';
import { INavinetAuthDoc } from './auth-claim.type';

@Injectable()
export class AuthClaimService {
  constructor(private dss: DataSourceService) {}

  getProcedureCodes() {
    return isAlpha() ? ['W1894', 'W1895', 'S5160', 'S5161'] : ['W1759', 'W1760', 'W1761', 'W1762', 'W1764'];
  }

  getCodeMap() {
    return {
      W1759: 'Hot',
      W1760: 'Frozen',
      W1761: 'Sandwich',
      W1762: 'Emergency',
      W1764: 'Special',
      //
      W1894: 'Installation',
      W1895: 'Maintenance',
      S5160: 'Installation',
      S5161: 'Maintenance',
      //
      A9279: 'Wireless Monitoring',
      A9280: 'Fall detector / GPS Mobile',
    };
  }

  codeDisplayExpr = (item: any) => {
    return item ? item._id + ' - ' + this.getCodeMap()[item._id] : undefined;
  };

  buildFacilityDataSource(types: string[], grouped: boolean) {
    const so = this.dss.getStoreOptions(Facility, undefined, false);
    so.customFilter = {
      where: {
        ...(!isEmpty(types) ? { type: { inq: types } } : {}),
        status: { neq: 'INACTIVE' },
        shortname: { nin: ['N/A', 'MONT'] }, // TODO: remove hardcode
      },
      order: ['typeOrder DESC', 'type', 'name'],
    };

    const dso: DataSourceOptions = {
      store: new CustomStore(so),
      postProcess(recs: any[]) {
        if (grouped) {
          recs = Object.entries(groupBy(recs, 'type')) //
            .map(([key, items]) => ({ key, items }));
        }
        return recs;
      },
    } as DataSourceOptions;

    return dso;
  }

  buildCodeDso(dss: DataSourceService) {
    const col = 'ExportsAuthsDataCache';
    const aggregate = [
      {
        $match: {
          _inactive: { $ne: true },
          // _valid: true,
        },
      },
      {
        $group: { _id: '$Code' },
      },
      {
        $sort: { _id: 1 },
      },
    ];

    const store = new CustomStore({
      useDefaultSearch: true,
      cacheRawData: false,
      load: async (loadOptions: LoadOptions): Promise<any> => {
        return gqlMongoLoad(dss, col, loadOptions, aggregate).toPromise();
      },
      totalCount: async (loadOptions: LoadOptions): Promise<number> => {
        return gqlMongoCount(dss, col, loadOptions, aggregate).toPromise();
      },
      byKey: async (key: any | string | number): Promise<any> => {
        return gqlMongoByKey(dss, col, key).toPromise();
      },
    });
    return { store } as DataSourceOptions;
  }

  getResourceDataSet() {
    return [
      {
        text: 'Hot',
        id: 'a:W1759',
        color: '#da756b',
      },
      {
        text: 'Frozen',
        id: 'a:W1760',
        color: '#da756b',
      },
      {
        text: 'Sandwich',
        id: 'a:W1761',
        color: '#da756b',
      },
      {
        text: 'Emergency',
        id: 'a:W1762',
        color: '#da756b',
      },
      {
        text: 'Special',
        id: 'a:W1764',
        color: '#da756b',
      },
      {
        text: 'Hot',
        id: 's:W1759',
        color: '#2eb3d2',
      },
      {
        text: 'Frozen',
        id: 's:W1760',
        color: '#2eb3d2',
      },
      {
        text: 'Special',
        id: 's:W1764',
        color: '#2eb3d2',
      },
      {
        text: 'Hot',
        id: 's?:W1759',
        color: '#265a78',
      },
      {
        text: 'Frozen',
        id: 's?:W1760',
        color: '#265a78',
      },
      {
        text: 'Special',
        id: 's?:W1764',
        color: '#265a78',
      },
      {
        text: 'Hot',
        id: 'c:W1759',
        color: '#c266c9',
      },
      {
        text: 'Frozen',
        id: 'c:W1760',
        color: '#c266c9',
      },
      {
        text: 'Special',
        id: 'c:W1764',
        color: '#c266c9',
      },
    ];
  }

  getAuthRRule(auth: INavinetAuthDoc) {
    const _map1 = {
      Su: RRule.SU,
      M: RRule.MO,
      T: RRule.TU,
      W: RRule.WE,
      Th: RRule.TH,
      F: RRule.FR,
      Sa: RRule.SA,
    };

    const _map2 = {
      yearly: RRule.YEARLY,
      monthly: RRule.MONTHLY,
      weekly: RRule.WEEKLY,
    };

    const from = utc(auth.StartDT);
    const to = utc(auth.EndDT);

    const rOpts: Partial<Options> = {
      freq: _map2[auth.Frequency.toLowerCase()],
      byweekday: compact(
        Object.entries(oc(auth)._manifest({}))
          .filter(([k, v]) => !isNil(v) && v > 0)
          .map(([k, v]) => _map1[k]),
      ),
      dtstart: from.clone().hours(12).toDate(),
      until: to.clone().hours(12).toDate(),
    };

    return new RRule(rOpts);
  }

  getUnitsByDates(auths: INavinetAuthDoc[], dates: string[]) {
    return sum(
      dates.map(d => {
        const dow = moment(d).format('dd').toUpperCase();
        const auth = auths.find(
          a =>
            utc(d).toDate().getTime() >= utc(a.StartDT).toDate().getTime() &&
            utc(d).toDate().getTime() <= utc(a.EndDT).toDate().getTime(),
        );

        return oc(auth)._manifestNormalized[dow](0);
      }),
    );
  }
}
